import {
  AppBar,
  Badge,
  Container,
  Divider,
  IconButton,
  SvgIcon,
  Toolbar,
  Typography,
} from '@mui/material';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/ArrowLeft.svg';
import { ReactComponent as CartIcon } from 'assets/icons/Cart.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/Close.svg';
import { selectCartItems } from 'features/main/routes/Cart/slice';
import { FC, Fragment, MouseEventHandler, ReactNode } from 'react';
import { isMobile } from 'react-device-detect';
import { LinkProps } from 'react-router-dom';
import { WEB_IN_APP_EVENT } from 'shared/constants/common';
import { useWebInApp } from 'shared/contexts/WebInAppContext/WebInAppContext';
import useNavigateBack from 'shared/hooks/useNavigateBack';
import { useTypedSelector } from 'shared/redux/store';
import { logEvent } from 'shared/utils/gtm';
import { postMessage } from 'shared/utils/postMessage';
import HideOnScroll from '../HideOnScroll/HideOnScroll';
import LinkWithQuery from '../LinkWithQuery/LinkWithQuery';

export type PageHeaderProps = {
  title?: string | ReactNode;
  backLink?: LinkProps['to'];
  backClick?: MouseEventHandler<HTMLButtonElement>;
  hideRightBtn?: boolean;
  hideLeftBtn?: boolean;
  root?: boolean;
};
const PageHeader: FC<PageHeaderProps> = ({
  title = process.env.REACT_APP_NAME,
  backLink,
  backClick,
  hideLeftBtn = false,
  hideRightBtn = false,
  root = false,
}) => {
  const navigateBack = useNavigateBack();
  const cartItems = useTypedSelector(selectCartItems);
  const { isWebInApp } = useWebInApp();

  const handleClickBack: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (root && isMobile) {
      postMessage(WEB_IN_APP_EVENT.CLOSE);
    } else if (backClick) {
      backClick(e);
    } else {
      navigateBack();
    }
  };

  return (
    <HideOnScroll>
      <AppBar
        position='fixed'
        color='transparent'
        elevation={0}
        className='md:!sticky'
      >
        <Container maxWidth='sm' disableGutters>
          <Toolbar>
            {hideLeftBtn || (root && (!isMobile || !isWebInApp)) ? (
              <Fragment />
            ) : backLink ? (
              <IconButton
                component={LinkWithQuery}
                to={backLink}
                color='inherit'
                aria-label='back'
                sx={{ mr: 2 }}
              >
                <SvgIcon
                  fontSize='small'
                  component={ArrowLeftIcon}
                  inheritViewBox
                />
              </IconButton>
            ) : (
              <IconButton
                onClick={handleClickBack}
                color='inherit'
                aria-label='back'
                sx={{ mr: 2 }}
              >
                <SvgIcon
                  fontSize='small'
                  component={root && isMobile ? CloseIcon : ArrowLeftIcon}
                  inheritViewBox
                />
              </IconButton>
            )}
            <Typography
              variant='h6'
              align='center'
              component='div'
              noWrap
              sx={{
                flexGrow: 1,
                paddingRight: hideRightBtn ? 5 : 0,
                paddingLeft:
                  hideLeftBtn || (root && (!isMobile || !isWebInApp)) ? 5 : 0,
              }}
            >
              {title ?? process.env.REACT_APP_NAME}
            </Typography>
            {!hideRightBtn && (
              <IconButton
                component={LinkWithQuery}
                to='/cart'
                color='inherit'
                aria-label='cart'
                sx={{ ml: 2 }}
                onClick={() =>
                  logEvent('Category_detail_click_Mycart', {
                    screen: document.title,
                  })
                }
              >
                <Badge
                  badgeContent={cartItems.length}
                  invisible={cartItems.length <= 0}
                  color='error'
                >
                  <SvgIcon
                    fontSize='small'
                    component={CartIcon}
                    inheritViewBox
                  />
                </Badge>
              </IconButton>
            )}
          </Toolbar>
          <Divider />
        </Container>
      </AppBar>
    </HideOnScroll>
  );
};

export default PageHeader;
